import { createApi, FetchArgs, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  formatManyContentItems,
  prepareHeaders,
  queryStringBuilder,
  responseHandler
} from '@/redux/helpers';
import type { IContent } from '@/@types/cms';
import { type IContentExtended, expandChildren } from '@/lib/helpers/expandChildren';
import { baseQueryWithCircuitBreaker } from './baseQueries/baseQueryWithCircuitBreaker';
import { baseQueryWithRetry } from './baseQueries/baseQueryWithRetry';
import { baseQueryWithTimeout } from './baseQueries/BaseQueryWithTimeout';
import { isLocal } from '@/utils/helpers';
import { RootState } from '../store';
import { simpleHash } from '@/utils/helpers';

export type GetContentQuery = {
  expand?: Array<string>;
  select?: Array<string>;
  userId?: string;
  tCode?: string;
  continuationToken?: string;
  enableWMSPrice?: string;
};

type GetContentResponse = IContent & {
  [key: string]: unknown;
};

type GetChildrenQuery = GetContentQuery & {
  top?: number;
};

export type GetChildrenResponse = {
  children: Array<IContent>;
  meta?: {
    ['x-epi-continuation']?: string | null;
  };
};

const baseUrl = import.meta.env.VITE_CONTENT_DELIVERY_API;
const DEFAULT_CONTENT_API_TIMEOUT_MS = Number(import.meta.env.VITE_CONTENT_API_TIMEOUT_MS) || 30000;

const baseFetchQuery = fetchBaseQuery({
  baseUrl: baseUrl,
  prepareHeaders,
  responseHandler,
  credentials: 'include',
  mode: 'cors'
});

export const contentDeliveryApi = createApi({
  reducerPath: 'contentDeliveryApi',
  tagTypes: ['Content', 'Global'],
  baseQuery: baseQueryWithRetry(
    baseQueryWithCircuitBreaker(
      import.meta.env.VITE_CONTENT_DELIVERY_API,
      baseQueryWithTimeout(
        import.meta.env.VITE_CONTENT_DELIVERY_API,
        async (args, api, extraOptions) => {
          const state = api.getState() as RootState;
          const userName = state.auth?.name || 'ANON';
          const hashedUserId = simpleHash(userName);
          const isAuthenticated = !!state.auth?.token;

          let url = typeof args === 'string' ? args : args.url;

          // Add userId to initial request if not present
          if (!url.includes('userId=')) {
            const separator = url.includes('?') ? '&' : '?';
            url = `${url}${separator}userId=${hashedUserId}`;
          }

          const updatedArgs = typeof args === 'string' ? url : { ...args, url };

          const result = await baseFetchQuery(updatedArgs, api, extraOptions);

          // Handle 302 redirect
          if (isAuthenticated && result.meta?.response?.redirected) {
            const redirectUrl = result.meta.response.url;
            // Add userId to redirected URL if not present
            const finalUrl = !redirectUrl.includes('userId=')
              ? `${redirectUrl}${redirectUrl.includes('?') ? '&' : '?'}userId=${hashedUserId}`
              : redirectUrl;

            // Make a new request to the redirected URL with userId
            return baseFetchQuery(
              {
                url: finalUrl,
                method: 'GET',
                headers: {
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  Pragma: 'no-cache',
                  Expires: '0'
                }
              },
              api,
              extraOptions
            );
          }

          return result;
        },
        DEFAULT_CONTENT_API_TIMEOUT_MS
      )
    )
  ),
  endpoints: builder => ({
    getContent: builder.query<GetContentResponse, { contentLink: string; query?: GetContentQuery }>(
      {
        query: ({ contentLink, query = {} }) =>
          `api/episerver/v3.0/content/${contentLink}${queryStringBuilder(query)}`,
        transformResponse: (response: IContentExtended) => expandChildren(response)
      }
    ),

    getChildren: builder.query<
      GetChildrenResponse,
      { contentLink: string; query?: GetChildrenQuery; headers?: { [key: string]: string } }
    >({
      query: ({ contentLink, query = {}, headers = {} }) => ({
        url: `api/episerver/v3.0/content/${contentLink}/children${queryStringBuilder(query)}`,
        headers
      }),
      transformResponse: (response: Array<IContent>, meta) => {
        const headers: { [key: string]: string } = {};
        const epiContinuation = meta?.response?.headers.get('x-epi-continuation');

        if (epiContinuation) {
          headers['x-epi-continuation'] = epiContinuation;
        }

        return {
          children: response,
          meta: headers
        };
      }
    }),

    getAncestors: builder.query<Array<IContent>, { contentLink: string; query?: GetContentQuery }>({
      query: ({ contentLink, query = {} }) =>
        `api/episerver/v3.0/content/${contentLink}/ancestors${queryStringBuilder(query)}`
    }),

    getContentByFriendlyUrl: builder.query<
      IContentExtended,
      { friendlyUrl: string; query?: GetContentQuery; userId: string }
    >({
      query: ({ friendlyUrl, query = {} }) => `${friendlyUrl}${queryStringBuilder(query)}`,
      transformResponse: (response: IContentExtended) => expandChildren(response),
      providesTags: (_, __, { friendlyUrl, userId }) => [
        { type: 'Content', id: friendlyUrl + userId }
      ]
    }),

    getGlobalElementDirect: builder.query<
      IContentExtended | undefined,
      { name: string; query?: GetContentQuery; userId: string }
    >({
      query: ({ name, query = {} }) => `api/client/content/${name}${queryStringBuilder(query)}`
    }),

    getGlobalManyElements: builder.query<
      { [key: string]: IContentExtended },
      { names: string[]; query?: GetContentQuery; userId: string }
    >({
      queryFn: ({ names }, _, __, baseQuery) => {
        const p = baseQuery(`api/client/content/many?${names.map(a => `items=${a}`).join('&')}`);
        const rp = Promise.resolve(p);
        return rp.then(queryReturnValue => {
          if (queryReturnValue?.error) return queryReturnValue;

          return formatManyContentItems(names, queryReturnValue?.data as Array<IContentExtended>);
        });
      },
      providesTags: (_, __, { names, userId }) => [
        { type: 'Global', id: `${names.join('_')}_${userId}` }
      ]
    }),

    getGlobalElement: builder.query<
      IContentExtended,
      { name: string; query?: GetContentQuery; userId: string }
    >({
      queryFn: ({ name, query, userId }, _, __, baseQuery) => {
        const p = baseQuery(
          `api/client/content/${name}${queryStringBuilder({ ...(query || {}), userId: userId })}`
        );
        const rp = Promise.resolve(p);
        return rp.then(queryReturnValue => {
          if (
            ((queryReturnValue.error?.status == 'PARSING_ERROR' ||
              queryReturnValue.error?.status == 404) &&
              queryReturnValue.meta?.response?.url) ||
            (!isLocal() &&
              !queryReturnValue.meta?.response?.url?.includes(name) &&
              (queryReturnValue.meta?.request?.headers?.keys.length ?? 0) == 0)
          ) {
            return Promise.resolve(
              baseQuery({
                url:
                  queryReturnValue.meta?.response?.url +
                  queryStringBuilder({ ...(query || {}), userId: userId }),
                params: { ...queryReturnValue?.meta?.request }
              } as FetchArgs)
            ).then(newReturnvalue => {
              return { data: expandChildren(newReturnvalue.data as IContentExtended) };
            });
          }
          return { data: expandChildren(queryReturnValue.data as IContentExtended) };
        });
      },
      providesTags: (_, __, { name, userId }) => [{ type: 'Global', id: name + userId }]
    }),

    logOut: builder.mutation<void, void>({
      query: () => '/util/logout',
      invalidatesTags: ['Content', 'Global']
    })
  })
});

export const {
  useGetContentQuery,
  useLazyGetContentQuery,
  useGetChildrenQuery,
  useLazyGetChildrenQuery,
  useGetAncestorsQuery,
  useGetContentByFriendlyUrlQuery,
  useGetGlobalElementQuery,
  useGetGlobalManyElementsQuery,
  useLogOutMutation
} = contentDeliveryApi;
